import {
	BasicType,
	getNodeIdxFromClassName,
	getNodeTypeFromClassName,
	MERGE_TAG_CLASS_NAME,
} from '@bay-email/core';
import { camelCase } from 'lodash';
import React from 'react';
import { isTextBlock } from './isTextBlock';
import { MergeTagBadge } from './MergeTagBadge';
import {
	ContentEditableType,
	DATA_CONTENT_EDITABLE_IDX,
	DATA_CONTENT_EDITABLE_TYPE,
} from '@/constants';
import {
	getContentEditableIdxFromClassName,
	getContentEditableTypeFromClassName,
} from './contenteditable';
import { getContentEditableClassName } from './getContentEditableClassName';
import { isNavbarBlock } from './isNavbarBlock';
import { isTableBlock } from './isTableBlock';
import { useEditableRenderer } from '@/components/Provider/EditableRendererProvider';

const domParser = new DOMParser();
const VALID_ATTRIBUTE_NAME = /^[a-zA-Z_:][a-zA-Z0-9:._-]*$/;

export function getChildSelector(selector: string, index: number) {
	return `${selector}-${index}`;
}

export interface HtmlStringToReactNodesOptions {
	enabledMergeTagsBadge: boolean;
	// Resolves a merge-tag key ("user.licenseNumber") to its real current value, if
	// known, so a badge chip displays real data instead of its field label while
	// staying a locked/underlined/clickable badge. Undefined for a key it can't
	// resolve leaves that badge showing its label.
	//
	// `blockIdx` is the block the chip sits in. "listing.*" needs it: an attached
	// listing is per-block context and this stage is flattened HTML, so the key
	// alone cannot say which listing to read.
	resolveMergeTagDisplayValue?: (
		tagKey: string,
		blockIdx?: string | null,
	) => string | undefined;
}

export function HtmlStringToReactNodes(
	content: string,
	option: HtmlStringToReactNodesOptions,
) {
	let doc = domParser.parseFromString(content, 'text/html'); // The average time is about 1.4 ms
	hoistHeadStylesIntoBody(doc);

	const isInsideRawBlock = (node: Element) => {
		const blockNode = node.closest('.email-block');
		if (!blockNode) return false;
		return getNodeTypeFromClassName(blockNode.classList) === BasicType.RAW;
	};

	[...doc.querySelectorAll('.email-block')].forEach(blockNode => {
		if (getNodeTypeFromClassName(blockNode.classList) !== BasicType.RAW) return;

		const rawHtml = blockNode.innerHTML;

		if (!/(<html\b|<head\b|<body\b)/i.test(rawHtml)) return;

		const rawDoc = domParser.parseFromString(rawHtml, 'text/html');
		const headStyles = [...rawDoc.head.querySelectorAll('style,link')]
			.map(node => node.outerHTML)
			.join('');
		blockNode.innerHTML = `${headStyles}${rawDoc.body.innerHTML}`;
	});

	[...doc.getElementsByTagName('a')].forEach(node => {
		if (isInsideRawBlock(node)) return;
		node.setAttribute('tabIndex', '-1');
	});
	// The chip resolver needs to know which block it is resolving for — see the
	// options doc above. The idx lives on the owning .email-block's class list.
	const resolverFor = (node: Element) => {
		const resolve = option.resolveMergeTagDisplayValue;
		if (!resolve) return undefined;
		const blockNode = node.closest('.email-block');
		const blockIdx = blockNode ? getNodeIdxFromClassName(blockNode.classList) : null;
		return (tagKey: string) => resolve(tagKey, blockIdx);
	};

	[...doc.querySelectorAll(`.${MERGE_TAG_CLASS_NAME}`)].forEach(child => {
		if (isInsideRawBlock(child)) return;
		const editNode = child.querySelector('div');
		if (editNode) {
			if (option.enabledMergeTagsBadge) {
				editNode.innerHTML = MergeTagBadge.transform(
					editNode.innerHTML,
					undefined,
					resolverFor(editNode),
				);
			}
		}
	});

	// Transform merge tags in editable rich-text blocks that don't use
	// bay-email-merge-tag-container (text + table cell content).
	if (option.enabledMergeTagsBadge) {
		[...doc.querySelectorAll('.email-block')].forEach(blockNode => {
			if (!(blockNode instanceof Element)) return;
			if (isInsideRawBlock(blockNode)) return;
			const blockType = getNodeTypeFromClassName(blockNode.classList);

			if (isTextBlock(blockType)) {
				const editNode = blockNode.querySelector('div');
				if (editNode) {
					editNode.innerHTML = MergeTagBadge.transform(
						editNode.innerHTML,
						undefined,
						resolverFor(editNode),
					);
				}
				return;
			}

			if (isTableBlock(blockType)) {
				blockNode.querySelectorAll('td').forEach(tdNode => {
					tdNode.innerHTML = MergeTagBadge.transform(
						tdNode.innerHTML,
						undefined,
						resolverFor(tdNode),
					);
				});
			}
		});
	}

	const reactNode = (
		<RenderReactNode
			selector={'0'}
			node={doc.documentElement}
			index={0}
		/>
	);

	return reactNode;
}

function hoistHeadStylesIntoBody(doc: Document) {
	const headNodes = [...doc.head.querySelectorAll('style,link[rel="stylesheet"]')];
	if (headNodes.length === 0) return;

	const fragment = doc.createDocumentFragment();

	headNodes.forEach(node => {
		if (node.tagName.toLowerCase() === 'style') {
			fragment.appendChild(node.cloneNode(true));
			return;
		}

		const href = node.getAttribute('href');
		if (!href) return;

		const styleNode = doc.createElement('style');
		styleNode.textContent = `@import url("${href}");`;
		fragment.appendChild(styleNode);
	});

	doc.body.prepend(fragment);
}

const RenderReactNode = React.memo(function ({
	node,
	index,
	selector,
}: {
	node: HTMLElement;
	index: number;
	selector: string;
}): React.ReactElement {
	const { Editable } = useEditableRenderer();
	const attributes: { [key: string]: string } = {
		'data-selector': selector,
	};
	node.getAttributeNames?.().forEach(att => {
		if (att && VALID_ATTRIBUTE_NAME.test(att)) {
			attributes[att] = node.getAttribute(att) || '';
		}
	});

	if (node.nodeType === Node.COMMENT_NODE) return <></>;

	if (node.nodeType === Node.TEXT_NODE) {
		return <>{node.textContent}</>;
	}

	if (node.nodeType === Node.ELEMENT_NODE) {
		const tagName = node.tagName.toLowerCase();
		if (tagName === 'meta') return <></>;

		if (tagName === 'style') {
			return createElement(tagName, {
				key: index,
				...attributes,
				dangerouslySetInnerHTML: { __html: node.textContent },
			});
		}

		const blockType = getNodeTypeFromClassName(node.classList);
		const idx = getNodeIdxFromClassName(node.classList);

		if (blockType) {
			if (idx) {
				makeStandardContentEditable(node, blockType, idx);
			}
			makeBlockNodeContentEditable(node);
		}

		if (
			Editable &&
			attributes['contenteditable'] === 'true' &&
			attributes[DATA_CONTENT_EDITABLE_TYPE] === ContentEditableType.RichText &&
			attributes[DATA_CONTENT_EDITABLE_IDX]
		) {
			return (
				<Editable
					key={attributes['data-selector'] || index}
					idx={attributes[DATA_CONTENT_EDITABLE_IDX]}
					type={ContentEditableType.RichText}
					tag={tagName}
					attributes={attributes}
					style={getStyle(node.getAttribute('style'))}
					html={node.innerHTML}
				/>
			);
		}

		if (attributes['contenteditable'] === 'true') {
			// Use a stable key based on data-selector to prevent React from recreating the element
			// Using performance.now() causes React to destroy and recreate the DOM node on every render,
			// which loses the user's text selection
			return createElement(tagName, {
				key: attributes['data-selector'] || index,
				...attributes,
				style: getStyle(node.getAttribute('style')),
				dangerouslySetInnerHTML: { __html: node.innerHTML },
			});
		}

		const reactNode = createElement(tagName, {
			key: index,
			...attributes,
			style: getStyle(node.getAttribute('style')),
			children:
				node.childNodes.length === 0
					? null
					: [...node.childNodes].map((n, i) => (
							<RenderReactNode
								selector={getChildSelector(selector, i)}
								key={i}
								node={n as any}
								index={i}
							/>
						)),
		});

		return <>{reactNode}</>;
	}

	return <></>;
});

function getStyle(styleText: string | null) {
	if (!styleText) return undefined;
	return styleText.split(';').reduce((a: any, b: any) => {
		const arr = b.split(/\:(?!\/)/);
		if (arr.length < 2) return a;
		// Remove !important since React inline styles don't support it
		const value = arr[1].trim().replace(/\s*!important\s*$/, '');
		a[camelCase(arr[0].trim())] = value;
		return a;
	}, {});
}

function createElement(
	type: string,
	props?: React.ClassAttributes<Element> & {
		style?: {} | undefined;
		children?: JSX.Element[] | null;
		key: string | number;
		tabIndex?: string;
		class?: string;
		role?: string;
		src?: string;
		dangerouslySetInnerHTML?: any;
	},
) {
	if (props?.class && props.class.includes('email-block')) {
		const blockType = getNodeTypeFromClassName(props.class);
		if (![BasicType.TEXT].includes(blockType as any)) {
			props.role = 'tab';
			props.tabIndex = '0';
		}
		props.key = props.key + props.class;
	}

	return React.createElement(type, props);
}

function makeBlockNodeContentEditable(node: ChildNode) {
	if (!(node instanceof Element)) return;
	const type = getContentEditableTypeFromClassName(node.classList);
	const idx = getContentEditableIdxFromClassName(node.classList);

	if (isTextBlock(type)) {
		const editNode = node.querySelector('div');
		if (editNode) {
			editNode.setAttribute('contentEditable', 'true');
			editNode.setAttribute(DATA_CONTENT_EDITABLE_TYPE, ContentEditableType.RichText);
			editNode.setAttribute(DATA_CONTENT_EDITABLE_IDX, idx);
		}
	} else if (isNavbarBlock(type)) {
		node.setAttribute('contentEditable', 'true');
		node.setAttribute(DATA_CONTENT_EDITABLE_TYPE, ContentEditableType.Text);
		node.setAttribute(DATA_CONTENT_EDITABLE_IDX, idx);
	} else if (isTableBlock(type)) {
		const trNodes = node.querySelectorAll('tr');
		trNodes.forEach((trNode, trIndex) => {
			const tdNodes = trNode.querySelectorAll('td');
			tdNodes.forEach((tdNode, tdIndex) => {
				const _idx = idx.replace(
					'data.value.content',
					`data.value.tableSource.${trIndex}.${tdIndex}.content`,
				);
				tdNode.setAttribute('contentEditable', 'true');
				tdNode.setAttribute(DATA_CONTENT_EDITABLE_TYPE, ContentEditableType.RichText);
				tdNode.setAttribute(DATA_CONTENT_EDITABLE_IDX, _idx);
			});
		});
	}

	node.childNodes.forEach(makeBlockNodeContentEditable);
}

function makeStandardContentEditable(node: HTMLElement, blockType: string, idx: string) {
	if (isTextBlock(blockType) || isTableBlock(blockType)) {
		node.classList.add(
			...getContentEditableClassName(blockType, `${idx}.data.value.content`),
		);
	}
	if (isNavbarBlock(blockType)) {
		node.querySelectorAll('.mj-link').forEach((anchor, index) => {
			anchor.classList.add(
				...getContentEditableClassName(
					blockType,
					`${idx}.data.value.links.${index}.content`,
				),
			);
		});
	}
}

// Ending tags
// Some of the mjml components are "ending tags". These are mostly the components that will contain text contents, like mj-text or mj-buttons. These components can contain not only text, but also any HTML content, which will be completely unprocessed and left as it is. This means you cannot use other MJML components inside them, but you can use any HTML tag, like <img> or <a>.

// This has a little downside : The content is not modified at all, this means that the text won't be escaped, so if you use characters that are used to define html tags in your text, like < or >, you should use the encoded characters &lt; and &lt;. If you don't, sometimes the browser can be clever enough to understand that you're not really trying to open/close an html tag, and display the unescaped character as normal text, but this may cause problems in some cases. For instance, this will likely cause problems if you use the minify option, mj-html-attributes or an inline mj-style, because these require the html to be re-parsed internally. If you're just using the minify option, and really need to use the < > characters, i.e. for templating language, you can also avoid this problem by wrapping the troublesome content between two <!-- htmlmin:ignore --> tags.

// Here is the list of all ending tags : - mj-accordion-text - mj-accordion-title - mj-button - mj-navbar-link - mj-raw - mj-social-element - mj-text - mj-table
